/**
 * Select > Refine Edge: re-estimates alpha near a selection's edge from the
 * image's own detail instead of the coarse outline the user drew. Every
 * function is pure: it reads its inputs and returns a new `Uint8Array`,
 * never mutating `coverage`, `trimap` or `image.pixels`.
 */
import { borderCoverage, featherCoverage, smoothCoverage } from './modify';
import type { ColorSelectImage } from './colorSelect';

/** Values of a Refine Edge trimap. */
export const TRIMAP_UNKNOWN = 128;
export const TRIMAP_FOREGROUND = 255;
export const TRIMAP_BACKGROUND = 0;

export interface RefineEdgeOptions {
  /** Radius in pixels of the band around the edge that gets re-estimated. */
  radius: number;
  /** Rounds the edge before refining, in pixels. */
  smooth: number;
  /** Softens the result, in pixels. */
  feather: number;
  /** -1..1; positive hardens the edge towards a hard cut, negative softens it. */
  contrast: number;
  /** -1..1 fraction of the radius the edge moves; positive expands. */
  shiftEdge: number;
}

/** Regularisation on 0..1 alpha/guide values, per He/Sun/Tang's `eps`. */
const GUIDED_EPS = 1e-4;

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function toByte(fraction: number): number {
  return Math.round(clamp01(fraction) * 255);
}

function isDegenerate(width: number, height: number, coverageLength: number): boolean {
  return width <= 0 || height <= 0 || coverageLength !== width * height;
}

/** Luma guide for the filter; only a single greyscale channel is needed. */
function greyscaleGuide(pixels: Uint8Array, size: number): Float64Array {
  const guide = new Float64Array(size);
  for (let i = 0; i < size; i += 1) {
    const o = i * 4;
    guide[i] = (0.299 * pixels[o] + 0.587 * pixels[o + 1] + 0.114 * pixels[o + 2]) / 255;
  }
  return guide;
}

/**
 * Inclusive 2D summed-area table, one row/column larger than `values` on
 * every side so range sums (see `boxSum`) never need bounds-checked
 * subtraction at the table's own edges.
 */
function buildIntegral(values: Float64Array, width: number, height: number): Float64Array {
  const stride = width + 1;
  const table = new Float64Array(stride * (height + 1));
  for (let y = 0; y < height; y += 1) {
    const prevRow = y * stride;
    const row = prevRow + stride;
    for (let x = 0; x < width; x += 1) {
      table[row + x + 1] =
        table[row + x] + table[prevRow + x + 1] - table[prevRow + x] + values[y * width + x];
    }
  }
  return table;
}

/** Sum of `values` over the inclusive rectangle `[x0,x1] x [y0,y1]`. */
function boxSum(table: Float64Array, width: number, x0: number, x1: number, y0: number, y1: number): number {
  const stride = width + 1;
  return (
    table[(y1 + 1) * stride + (x1 + 1)] -
    table[y0 * stride + (x1 + 1)] -
    table[(y1 + 1) * stride + x0] +
    table[y0 * stride + x0]
  );
}

/**
 * Guided image filter (He, Sun, Tang, "Guided Image Filtering"): re-estimates
 * `alphaIn` at every `band` pixel as a local linear function of `guide`,
 * `a * guide + b`, fit by least squares over a `(2*windowRadius+1)` square
 * window. Box-filter means/variances are read from summed-area tables built
 * once up front, so the per-pixel cost is O(1) regardless of `windowRadius`
 * instead of O(windowRadius^2) for a naive sliding window. The classic
 * guided filter also box-filters the per-window `a`/`b` coefficients
 * themselves before applying them (so overlapping windows agree); that
 * second pass is not implemented here — each band pixel uses only the `a`/`b`
 * fit from its own centred window, per the spec this module was written to.
 */
function guidedFilterAlpha(
  guide: Float64Array,
  alphaIn: Float64Array,
  width: number,
  height: number,
  windowRadius: number,
  band: Uint8Array,
): Float64Array {
  const size = guide.length;
  const cross = new Float64Array(size);
  const guideSq = new Float64Array(size);
  for (let i = 0; i < size; i += 1) {
    cross[i] = guide[i] * alphaIn[i];
    guideSq[i] = guide[i] * guide[i];
  }
  const guideTable = buildIntegral(guide, width, height);
  const alphaTable = buildIntegral(alphaIn, width, height);
  const crossTable = buildIntegral(cross, width, height);
  const guideSqTable = buildIntegral(guideSq, width, height);

  const out = Float64Array.from(alphaIn);
  for (let y = 0; y < height; y += 1) {
    const y0 = Math.max(0, y - windowRadius);
    const y1 = Math.min(height - 1, y + windowRadius);
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      if (!band[i]) continue;
      const x0 = Math.max(0, x - windowRadius);
      const x1 = Math.min(width - 1, x + windowRadius);
      const count = (x1 - x0 + 1) * (y1 - y0 + 1);
      const meanG = boxSum(guideTable, width, x0, x1, y0, y1) / count;
      const meanA = boxSum(alphaTable, width, x0, x1, y0, y1) / count;
      const meanGA = boxSum(crossTable, width, x0, x1, y0, y1) / count;
      const meanGG = boxSum(guideSqTable, width, x0, x1, y0, y1) / count;
      const varG = meanGG - meanG * meanG;
      const covGA = meanGA - meanG * meanA;
      const a = covGA / (varG + GUIDED_EPS);
      const b = meanA - a * meanG;
      out[i] = clamp01(a * guide[i] + b);
    }
  }
  return out;
}

/**
 * S-curve around 0.5 (in 0..1 units) with steepness controlled by `contrast`:
 * `exponent = 2^(-contrast)` is < 1 for positive contrast (pushes values away
 * from 0.5, towards a hard cut) and > 1 for negative contrast (pulls values
 * towards 0.5, softening the cut); `contrast = 0` is the identity. Endpoints
 * 0 and 1 are always fixed, so only intermediate values move.
 */
function contrastCurve(value01: number, contrast: number): number {
  if (contrast === 0) return value01;
  const t = value01 - 0.5;
  const exponent = 2 ** -contrast;
  const magnitude = Math.abs(2 * t) ** exponent / 2;
  return clamp01(0.5 + Math.sign(t) * magnitude);
}

/**
 * Builds the boolean re-estimation band and forces trimap-certain pixels in
 * `alpha` (mutated in place; `alpha` is always this function's own buffer,
 * never the caller's `coverage`). With a trimap, the band is exactly the
 * `TRIMAP_UNKNOWN` pixels, independent of `radius`. Without one, the band is
 * the `2*radius`-thick strip around the current edge; a `radius <= 0` here
 * correctly yields an empty band (there is no meaningful width to derive),
 * which is what makes shiftEdge/contrast below become no-ops in that case
 * while still running, satisfying "skip the guided filter but still honour
 * smooth/feather/contrast/shift".
 */
function buildBand(
  alpha: Uint8Array,
  width: number,
  height: number,
  radius: number,
  trimap: Uint8Array | null,
): Uint8Array {
  const size = width * height;
  const band = new Uint8Array(size);
  const trimapValid = trimap !== null && trimap.length === size;

  if (trimapValid) {
    for (let i = 0; i < size; i += 1) {
      const t = trimap[i];
      if (t === TRIMAP_FOREGROUND) alpha[i] = 255;
      else if (t === TRIMAP_BACKGROUND) alpha[i] = 0;
      else band[i] = 1;
    }
    return band;
  }

  if (radius > 0) {
    const border = borderCoverage(alpha, width, height, 2 * radius);
    for (let i = 0; i < size; i += 1) band[i] = border[i] >= 128 ? 1 : 0;
  }
  return band;
}

export function refineEdge(
  image: ColorSelectImage,
  coverage: Uint8Array,
  trimap: Uint8Array | null,
  options: RefineEdgeOptions,
): Uint8Array {
  const { width, height, pixels } = image;
  if (isDegenerate(width, height, coverage.length)) return coverage.slice();
  const size = width * height;

  const alpha =
    options.smooth > 0 ? smoothCoverage(coverage, width, height, options.smooth) : coverage.slice();
  const band = buildBand(alpha, width, height, options.radius, trimap);

  let bandHasPixels = false;
  for (let i = 0; i < size && !bandHasPixels; i += 1) bandHasPixels = band[i] !== 0;

  if (options.radius > 0 && bandHasPixels) {
    const guide = greyscaleGuide(pixels, size);
    const alphaFraction = new Float64Array(size);
    for (let i = 0; i < size; i += 1) alphaFraction[i] = alpha[i] / 255;
    const windowRadius = Math.max(1, Math.round(options.radius));
    const refined = guidedFilterAlpha(guide, alphaFraction, width, height, windowRadius, band);
    for (let i = 0; i < size; i += 1) if (band[i]) alpha[i] = toByte(refined[i]);
  }

  if (options.shiftEdge !== 0 && bandHasPixels) {
    // Exact edge geometry would need to know the local gradient to convert a
    // pixel shift into an alpha delta; this uses the simplest correct
    // approximation instead, a flat offset in alpha units proportional to
    // shiftEdge, which is exact for a linear ramp and a reasonable stand-in
    // elsewhere.
    const shift = options.shiftEdge * 0.5;
    for (let i = 0; i < size; i += 1) {
      if (band[i]) alpha[i] = toByte(alpha[i] / 255 + shift);
    }
  }

  if (options.contrast !== 0 && bandHasPixels) {
    for (let i = 0; i < size; i += 1) {
      if (band[i]) alpha[i] = toByte(contrastCurve(alpha[i] / 255, options.contrast));
    }
  }

  return options.feather > 0 ? featherCoverage(alpha, width, height, options.feather) : alpha;
}
